<div class="wrapper-replay">

  <!-- Header & Breadcrumbs -->
  <div class="section-title">
    <div class="header-content-wrap">
      <div class="header-left">
        <a routerLink="/dashboard/visitor-intelligence" class="btn btn-back flex-shrink-0" [attr.aria-label]="'Back' | translate">
          <i class="fi-rr-arrow-alt-left"></i>
        </a>
        <div class="header-titles">
          <div class="title d-flex align-items-center gap-2" [attr.data-parent]="'Visitor Intelligence' | translate">
            <span>{{ "Session Replay" | translate }}</span>
          </div>
          <p class="subtitle text-muted mb-0">
            {{ "Review recorded visitor journeys, playback user interactions, and discover behavioral trends." | translate }}
          </p>
        </div>
      </div>
      <div class="header-right" *ngIf="!isLoading">
        <span class="header-pill-badge">
          <i class="fi fi-rr-users-alt"></i>
          <strong>{{ totalRecord }}</strong>
          <span>{{ "Total Recorded Visitors" | translate }}</span>
        </span>
      </div>
    </div>
  </div>

  <div class="section-main mt-3">

    <!-- KPI Metric Cards -->
    <div class="row g-3 mb-4 kpi-row" *ngIf="!isLoading && visitors.length > 0">
      <div class="col-6 col-lg-3">
        <div class="kpi-card">
          <div class="kpi-icon bg-blue-subtle text-primary">
            <i class="fi fi-rr-users-alt"></i>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">{{ "Filtered Visitors" | translate }}</div>
            <div class="kpi-value">{{ displayedMetrics.totalVisitors }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-lg-3">
        <div class="kpi-card">
          <div class="kpi-icon bg-green-subtle text-success">
            <i class="fi fi-rr-play-alt"></i>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">{{ "Total Sessions" | translate }}</div>
            <div class="kpi-value">{{ displayedMetrics.totalSessions }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-lg-3">
        <div class="kpi-card">
          <div class="kpi-icon bg-purple-subtle text-purple">
            <i class="fi fi-rr-refresh"></i>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">{{ "Avg. Sessions / Visitor" | translate }}</div>
            <div class="kpi-value">{{ displayedMetrics.avgSessions }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-lg-3">
        <div class="kpi-card">
          <div class="kpi-icon bg-amber-subtle text-amber">
            <i class="fi fi-rr-chart-pie"></i>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">{{ "Returning Rate" | translate }}</div>
            <div class="kpi-value">{{ displayedMetrics.returningRate }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dismissible Notice & Classification Guide -->
    <div class="modern-notice-banner mb-3" *ngIf="showInfoBanner">
      <div class="d-flex align-items-start gap-2 flex-grow-1">
        <div class="banner-icon mt-1">
          <i class="fi fi-rr-info"></i>
        </div>
        <div class="banner-text flex-grow-1">
          <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
            <strong>{{ "Visitor Intelligence & Engagement Guide" | translate }}:</strong>
            <span class="small guide-hint">{{ "Hover badges for detailed session metrics" | translate }}</span>
          </div>
          <div class="d-flex align-items-center gap-2 flex-wrap mt-2 guide-pill-row">
            <span class="guide-item" [title]="'Visitors with 5 or more recorded sessions across visits.' | translate">
              <span class="badge bg-purple-subtle text-purple border me-1">
                <i class="fi fi-rr-flame me-1"></i>{{ "Frequent (≥ 5)" | translate }}
              </span>
              <span class="guide-desc text-muted small">{{ "5+ recorded visits" | translate }}</span>
            </span>
            <span class="guide-item" [title]="'Visitors who returned for 2 to 4 recorded sessions.' | translate">
              <span class="badge bg-info-subtle text-info border me-1">
                <i class="fi fi-rr-refresh me-1"></i>{{ "Returning (2–4)" | translate }}
              </span>
              <span class="guide-desc text-muted small">{{ "2–4 visits" | translate }}</span>
            </span>
            <span class="guide-item" [title]="'First-time or single recorded visit.' | translate">
              <span class="badge bg-light text-muted border me-1">
                <i class="fi fi-rr-user me-1"></i>{{ "Single Visit (1)" | translate }}
              </span>
              <span class="guide-desc text-muted small">{{ "1 visit" | translate }}</span>
            </span>
          </div>
        </div>
      </div>
      <button type="button" class="btn-banner-dismiss" (click)="dismissNotice()" title="Dismiss notice">
        <i class="fi fi-rr-cross"></i>
      </button>
    </div>

    <!-- Warning if disabled -->
    <div class="alert alert-warning d-flex align-items-center mb-3" *ngIf="!sessionReplayEnabled">
      <i class="fi fi-rr-exclamation me-2"></i>
      <div>
        {{ "Session Replay is currently disabled. You can enable it from" | translate }}
        <a routerLink="/dashboard/visitor-intelligence" class="alert-link fw-semibold ms-1">{{ "Visitor Intelligence" | translate }}</a>.
      </div>
    </div>

    <!-- Inline Filter & Search Toolbar -->
    <div class="filter-toolbar mb-3">
      <div class="row g-2 align-items-center">
        <!-- Search -->
        <div class="col-12 col-lg-3">
          <div class="search-input-wrapper">
            <i class="fi fi-rr-search search-icon"></i>
            <input
              type="text"
              class="form-control form-control-sm search-input"
              [(ngModel)]="searchQuery"
              [placeholder]="'Search by Visitor ID...' | translate"
            />
            <button *ngIf="searchQuery" class="btn-clear-search" (click)="searchQuery = ''">
              <i class="fi fi-rr-cross-small"></i>
            </button>
          </div>
        </div>

        <!-- Date Segmented Filter -->
        <div class="col-12 col-sm-6 col-lg-3 col-xl-3">
          <div class="segmented-control w-100">
            <button
              type="button"
              class="segmented-btn"
              *ngFor="let f of DATE_FILTERS"
              [class.active]="selectedDateFilter.value === f.value"
              (click)="selectDateFilter(f)"
            >
              {{ f.display | translate }}
            </button>
          </div>
        </div>

        <!-- Behavior / Activity Filter Pills (Inline, No Wrap) -->
        <div class="col-12 col-sm-6 col-lg-6 col-xl-6 text-sm-end">
          <div class="d-flex align-items-center justify-content-start justify-content-sm-end gap-1 flex-nowrap duration-pill-group">
            <span class="small text-muted me-1 d-none d-xl-inline flex-shrink-0" [title]="'Filter visitors by return frequency' | translate">
              <i class="fi fi-rr-info me-1"></i>{{ "Filter:" | translate }}
            </span>
            <button
              *ngFor="let bf of BEHAVIOR_FILTERS"
              type="button"
              class="btn btn-xs duration-pill flex-shrink-0"
              [class.active]="activeBehaviorFilter === bf.value"
              (click)="setBehaviorFilter(bf.value)"
              [title]="bf.tooltip | translate"
            >
              {{ bf.label | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2 text-muted small">{{ "Fetching recorded visitors..." | translate }}</p>
    </div>

    <!-- Empty State: No Visitors Recorded -->
    <div *ngIf="!isLoading && visitors.length === 0" class="empty-state-card text-center py-5">
      <div class="empty-icon-circle mx-auto mb-3">
        <i class="fi fi-rr-users-alt"></i>
      </div>
      <h6 class="empty-title">{{ "No visitors recorded yet" | translate }}</h6>
      <p class="empty-desc text-muted mb-3">
        {{ "There are no recorded visitor journeys matching the selected date range." | translate }}
      </p>
      <button class="btn btn-sm btn-outline-primary" (click)="selectDateFilter(DATE_FILTERS[2]); setBehaviorFilter('all')">
        <i class="fi fi-rr-refresh me-1"></i> {{ "Reset Filters" | translate }}
      </button>
    </div>

    <!-- Empty State: Filter yielded no results -->
    <div *ngIf="!isLoading && visitors.length > 0 && filteredVisitors.length === 0" class="empty-state-card text-center py-5">
      <div class="empty-icon-circle mx-auto mb-3">
        <i class="fi fi-rr-search-alt"></i>
      </div>
      <h6 class="empty-title">{{ "No matching visitors" | translate }}</h6>
      <p class="empty-desc text-muted mb-3">
        {{ "No visitors match your search keyword or selected behavior filter." | translate }}
      </p>
      <button class="btn btn-sm btn-outline-secondary" (click)="searchQuery = ''; setBehaviorFilter('all')">
        {{ "Clear Search & Filters" | translate }}
      </button>
    </div>

    <!-- Visitors List (Identical High-Density Card Design as replay-sessions) -->
    <div *ngIf="!isLoading && filteredVisitors.length > 0">
      <div class="session-list-wrapper">
        <div
          class="session-row-card mb-2"
          *ngFor="let v of filteredVisitors"
          [id]="'visitor-' + v.visitor_id"
          (click)="viewSessions(v.visitor_id)"
        >
          <div class="row align-items-center g-3">

            <!-- Col 1: Visitor ID & Date -->
            <div class="col-12 col-md-3">
              <div class="d-flex align-items-center gap-2">
                <div class="session-play-avatar">
                  <i class="fi fi-rr-user"></i>
                </div>
                <div>
                  <div class="d-flex align-items-center gap-1">
                    <span class="session-id-pill font-monospace">#{{ shortId(v.visitor_id) }}</span>
                    <button
                      type="button"
                      class="btn-copy-icon"
                      (click)="copyText(v.visitor_id, v.visitor_id, $event)"
                      [title]="'Copy full Visitor ID' | translate"
                    >
                      <i class="fi" [ngClass]="copiedId === v.visitor_id ? 'fi-rr-check text-success' : 'fi-rr-copy'"></i>
                    </button>
                  </div>
                  <div class="session-date-time text-muted mt-1">
                    <i class="fi fi-rr-calendar me-1"></i>
                    <span>{{ v.last_seen | date: 'dd MMM yyyy, HH:mm' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Col 2: Sessions Count & Returning Badge -->
            <div class="col-12 col-md-4">
              <div class="page-meta-wrapper">
                <div class="d-flex align-items-center gap-2 mb-1">
                  <span class="badge bg-primary-subtle text-primary border-0 rounded-pill px-2 py-1 small">
                    <i class="fi fi-rr-play-alt me-1"></i>
                    {{ v.session_count }} {{ (v.session_count === 1 ? 'Session' : 'Sessions') | translate }}
                  </span>
                  <span class="badge bg-info-subtle text-info border-0 rounded-pill px-2 py-1 small" *ngIf="v.session_count > 1">
                    <i class="fi fi-rr-refresh me-1"></i>
                    {{ "Returning" | translate }}
                  </span>
                </div>
                <div class="url-breadcrumb-link text-truncate">
                  <i class="fi fi-rr-time-past text-muted me-1"></i>
                  <span class="text-muted small">{{ "First seen" | translate }}: {{ v.first_seen | date: 'dd MMM yyyy, HH:mm' }}</span>
                </div>
              </div>
            </div>

            <!-- Col 3: Engagement Level & Activity Span -->
            <div class="col-6 col-md-3">
              <div class="activity-meta">
                <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                  <span
                    class="badge rounded-pill px-2 py-1 small font-weight-bold"
                    [ngClass]="getVisitorEngagement(v).badgeClass"
                    [title]="getVisitorEngagement(v).tooltip | translate"
                  >
                    <i class="fi me-1" [ngClass]="getVisitorEngagement(v).icon"></i>
                    {{ getVisitorEngagement(v).label | translate }}
                  </span>
                </div>
                <div class="activity-meter d-flex align-items-center gap-1" [title]="'Activity Level' | translate">
                  <span
                    class="meter-dot"
                    *ngFor="let dot of getActivityDots(v)"
                    [class.active]="dot === 1"
                  ></span>
                  <span class="meter-caption ms-1 text-muted">
                    {{ getVisitorSpan(v.first_seen, v.last_seen) | translate }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Col 4: Action Button -->
            <div class="col-6 col-md-2 text-end">
              <button
                class="btn btn-sm btn-watch-replay"
                (click)="viewSessions(v.visitor_id); $event.stopPropagation()"
                [title]="'View visitor sessions' | translate"
              >
                <i class="fi fi-rr-play me-1"></i>
                <span>{{ "View Sessions" | translate }}</span>
              </button>
            </div>

          </div>
        </div>
      </div>

      <!-- Pagination Row -->
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 px-2 py-3 pagination-row" *ngIf="totalRecord > pageSize">
        <ngb-pagination
          [collectionSize]="totalRecord"
          [(page)]="currentPage"
          [pageSize]="pageSize"
          aria-label="Visitor pagination"
          [ellipses]="false"
          [rotate]="true"
          [maxSize]="5"
          (pageChange)="onPageChange()"
        ></ngb-pagination>
        <div class="pagination-info small text-muted">
          {{ "Showing" | translate }} <b>{{ start + 1 }}–{{ end }}</b> {{ "of" | translate }} <b>{{ totalRecord }}</b>
        </div>
      </div>
    </div>

  </div>
</div>
